import type { ComponentType, SVGAttributes } from 'react'
import IcBaselineAutoAwesome from '~icons/ic/baseline-auto-awesome'
import IcBaselineBrush from '~icons/ic/baseline-brush'
import IcBaselineCardGiftcard from '~icons/ic/baseline-card-giftcard'
import IcBaselineColorLens from '~icons/ic/baseline-color-lens'
import IcBaselineEdit from '~icons/ic/baseline-edit'
import IcBaselineLocalLibrary from '~icons/ic/baseline-local-library'
import IcBaselineMovie from '~icons/ic/baseline-movie'
import IcBaselineSmartphone from '~icons/ic/baseline-smartphone'
import IcBaselineTune from '~icons/ic/baseline-tune'
import MdiCat from '~icons/mdi/cat'
import MdiCityVariant from '~icons/mdi/city-variant'
import MdiFlower from '~icons/mdi/flower'
import MdiWeatherNight from '~icons/mdi/weather-night'

export type IconComponent = ComponentType<SVGAttributes<SVGSVGElement>>

// Gallery CDN URLs (marketing assets)
export const GALLERY_IMAGES = [
  {
    src: 'https://images-ng.pixai.art/gi/thumb/9c4731b1-e733-4d2d-84e4-bcf16c2773a2',
    category: 'anime',
  },
  {
    src: 'https://images-ng.pixai.art/gi/thumb/0fa9f782-e0d7-4cfa-8d14-6fc19fe8f5f9',
    category: 'anime',
  },
  {
    src: 'https://images-ng.pixai.art/gi/thumb/e73a4a4b-5a8e-4a4b-bd2a-8c9ca0b8ef6b',
    category: 'real',
  },
  {
    src: 'https://images-ng.pixai.art/gi/thumb/e69cef55-0617-418e-ab4c-9e5fc39e6d2c',
    category: 'anime',
  },
  {
    src: 'https://images-ng.pixai.art/gi/thumb/0b41572f-cd7d-4989-97db-a5acb5c27dad',
    category: 'real',
  },
  {
    src: 'https://images-ng.pixai.art/gi/thumb/92ddf07d-1c62-4e18-991c-d47fc96e6fce',
    category: 'sd',
  },
  {
    src: 'https://images-ng.pixai.art/gi/thumb/444a2ed7-1ff1-4b21-8ca2-d94a6b58be8f',
    category: 'background',
  },
  {
    src: 'https://images-ng.pixai.art/gi/thumb/449ec091-17bf-483a-a923-933de4002d12',
    category: 'anime',
  },
]

// Style keys for experience section
export const STYLE_KEYS = [
  'anime',
  'real',
  'sd',
  'watercolor',
  'dark',
  'fantasy',
] as const

// Prompt tag keys and icons for experience section
export const PROMPT_TAG_KEYS = [
  'sakura',
  'cyberpunk',
  'chibi-cat',
  'fantasy-landscape',
] as const

export const PROMPT_TAG_ICONS: Record<
  (typeof PROMPT_TAG_KEYS)[number],
  IconComponent
> = {
  sakura: MdiFlower,
  cyberpunk: MdiCityVariant,
  'chibi-cat': MdiCat,
  'fantasy-landscape': MdiWeatherNight,
}

// Aspect ratio data
export const ASPECT_RATIO_KEYS = [
  'square',
  'portrait',
  'landscape',
  'mobile',
] as const

export const ASPECT_RATIO_VALUES: Record<
  (typeof ASPECT_RATIO_KEYS)[number],
  string
> = {
  square: '1:1',
  portrait: '2:3',
  landscape: '3:2',
  mobile: '9:16',
}

// Gallery filter tab keys
export const GALLERY_TAB_KEYS = [
  'all',
  'anime',
  'real',
  'sd',
  'background',
] as const

// Premium badge indices (which gallery items show "Premium" badge)
export const PREMIUM_GALLERY_INDICES = [0, 2, 5]

// Step icons (indexed by step number)
export const STEP_ICONS: IconComponent[] = [
  IcBaselineBrush,
  IcBaselineEdit,
  IcBaselineAutoAwesome,
]

// Feature icons
export const FEATURE_ICONS: IconComponent[] = [
  IcBaselineLocalLibrary,
  IcBaselineMovie,
  IcBaselineTune,
  IcBaselineColorLens,
  IcBaselineCardGiftcard,
  IcBaselineSmartphone,
]

// Stats data
// Each stat has an anchor (known value at anchorDate) and a dailyGrowth rate.
// At render time we interpolate: current = anchor + dailyGrowth * daysSinceAnchor
// `countUpDurationMs` controls per-stat animation speed.
export interface StatConfig {
  /** Known value at `anchorTs` */
  anchor: number
  /** Unix timestamp (seconds) when `anchor` was measured */
  anchorTs: number
  /** Growth rate per second */
  growthPerSec: number
}

// 2026/3/10 時点 (ts: 1773080206)
export const STATS: StatConfig[] = [
  {
    anchor: 14_879_835,
    anchorTs: 1773080206,
    growthPerSec: 0.245,
  },
  {
    anchor: 4_349_809_660,
    anchorTs: 1773080206,
    growthPerSec: 85,
  },
  {
    anchor: 1_349_825,
    anchorTs: 1773080206,
    growthPerSec: 2.5 / 60, // 2.5 per minute
  },
]

// Testimonial initials
export const TESTIMONIAL_INITIALS = ['A', 'K', 'M']

// Loading step count
export const LOADING_STEP_COUNT = 4

// Plan display info keyed by tier (brand names, not translated)
export const PLAN_DISPLAY: Record<
  number,
  { name: string; featured?: boolean }
> = {
  0: { name: 'Free' },
  1: { name: 'Starter' },
  2: { name: 'Plus' },
  3: { name: 'Premium', featured: true },
}

// Nav link targets
export const NAV_LINK_KEYS = [
  'experience',
  'features',
  'pricing',
  'faq',
] as const

export const NAV_HREFS: Record<string, string> = {
  experience: '#experience',
  features: '#features',
  pricing: '#pricing',
  faq: '#faq',
}
